
import { SelectedWorkspaceCtx } from "../../contexts/SelectedWorkspaceCtx";
import { mainComps } from "../../main";
import { workspaceService } from "../../services/workspaceService";
import { workspaceStore, WorkspaceType } from "../../stores/workspaceStore";
import { WorkspaceDropdownItem } from "./WorkspaceDropdownItem";

export function WorkspaceDropdown({ createWSModalOpen, upgradeModalOpen, closeSidebar }:
  { createWSModalOpen: () => void; upgradeModalOpen: () => void; closeSidebar: () => void; }) {
  let $createWorkspaceBtn: null | HTMLElement = null;
  let $workspacesDDList: null | HTMLElement = null;
  let $workspaceDDButton: null | HTMLElement = null;
  let $workspaceDDCount: null | HTMLElement = null;
  let $allWorkspacesLink: null | HTMLElement = null;

  let workspaceStoreUnsubscribe: () => void;
  let selectedWSCtxUnsubscribe: () => void;

  async function handleCreateWorkspace() {
    try {
      mainComps.verifyOverlay.show();
      await workspaceService.validateCreate();

      mainComps.verifyOverlay.hide();
      createWSModalOpen();
    } catch (error) {
      console.error(error);
      mainComps.verifyOverlay.hide();
      upgradeModalOpen();
    }
  }

  function showSkeleton() {
    $workspaceDDButton!.innerHTML = `
      <div class="size-8 shrink-0 bg-gray-200 rounded-full dark:bg-neutral-700"></div>
      <span class="block ms-3 h-9 w-full truncate">
        <p class="h-4 bg-gray-200 rounded-full dark:bg-neutral-700" style="width: 70%;"></p>
        <p class="h-3 bg-gray-200 rounded-full dark:bg-neutral-700 mt-1" style="width: 80%;"></p>
      </span>
      <svg class="shrink-0 size-3.5 ms-auto" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
        stroke-linejoin="round">
        <path d="m7 15 5 5 5-5" />
        <path d="m7 9 5-5 5 5" />
      </svg>
    `;
  }

  function populate(workspaces: WorkspaceType[], isLoading: boolean) {
    if (isLoading) {
      showSkeleton();
      return;
    }

    $workspaceDDCount!.innerText = workspaces.length.toString();

    const fiveWorkspaces = workspaces.slice(0, 5);
    const { selectedWorkspace } = SelectedWorkspaceCtx;

    if (selectedWorkspace) {
      const isSelectedWorkspaceInTopFive = fiveWorkspaces.find(ws => ws.unique_workspace_id === selectedWorkspace?.unique_workspace_id);
      if (!isSelectedWorkspaceInTopFive) {
        fiveWorkspaces[4] = selectedWorkspace;
      }

      $workspaceDDButton!.innerHTML = `
        <div
          class="uppercase size-[32px] shrink-0 overflow-hidden rounded-full border border-1 border-gray-100 dark:border-neutral-700 flex items-center justify-center text-white dark:bg-neutral-900">
          ${selectedWorkspace?.logo_url ? `<img class="object-cover w-auto h-auto max-w-full max-h-[32px]"
            src="${selectedWorkspace.logo_url}"
            alt="Avatar">` : `<div class="size-8 rounded-full bg-[#6172f3] flex items-center justify-center">
            <span class="text-white text-lg uppercase">${selectedWorkspace ? selectedWorkspace.name[0] : "-"}</span>
          </div>`}
        </div>
        <span class="block ms-3 truncate" title="${selectedWorkspace ? selectedWorkspace.name : ""}">
          <span
            class="block text-sm font-medium text-gray-800 group-hover:text-blue-600 group-focus-hover:text-blue-600 dark:text-neutral-200 dark:group-hover:text-blue-600 dark:group-focus-hover:text-blue-600 truncate">
            ${selectedWorkspace ? selectedWorkspace.name : "No Workspace"}
          </span>
          <span class="block text-xs text-gray-500 dark:text-neutral-500 truncate">
            ${selectedWorkspace ? selectedWorkspace.domain ?? "No Custom Domain" : "No Custom Domain"}
          </span>
        </span>
        <svg class="shrink-0 size-3.5 ms-auto" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
          stroke-linejoin="round">
          <path d="m7 15 5 5 5-5" />
          <path d="m7 9 5-5 5 5" />
        </svg>
      `;
    }

    $workspacesDDList!.innerHTML = "";
    fiveWorkspaces.forEach(workspace => {
      const newWorkspaceDDItem = WorkspaceDropdownItem();
      $workspacesDDList!.insertAdjacentHTML("beforeend", newWorkspaceDDItem.render(workspace));
      setTimeout(newWorkspaceDDItem.init, 0);
    });

  }

  function init() {
    $createWorkspaceBtn = document.getElementById("sidebar-create-workspace");
    $workspacesDDList = document.getElementById("workspaces-dd-list");
    $workspaceDDButton = document.getElementById("sidebar-workspaces-dd-btn");
    $workspaceDDCount = document.getElementById("workspaces-dd-count");
    $allWorkspacesLink = document.getElementById("all-workspaces-link");

    if (!($createWorkspaceBtn && $workspacesDDList && $workspaceDDButton && $workspaceDDCount && $allWorkspacesLink)) {
      console.error("Can't find all the workspace dropdown elements");
      return;
    }

    $allWorkspacesLink.addEventListener("click", closeSidebar);

    $createWorkspaceBtn?.addEventListener("click", handleCreateWorkspace);

    populate(workspaceStore.workspaces, workspaceStore.loading);

    workspaceStoreUnsubscribe = workspaceStore.subscribe(({ workspaces, isLoading }) => populate(workspaces, isLoading));
    selectedWSCtxUnsubscribe = SelectedWorkspaceCtx.subscribe(() => populate(workspaceStore.workspaces, workspaceStore.loading)); // re-populate whenever selected workspace changes
  }

  function cleanUp() {
    if (workspaceStoreUnsubscribe && selectedWSCtxUnsubscribe) {
      workspaceStoreUnsubscribe();
      selectedWSCtxUnsubscribe();
    }
  }

  function render() {
    return /*html*/`
    <div class="px-7">
      <div id="sidebar-workspaces-dd" class="hs-dropdown [--auto-close:true] relative flex">
        <!-- Project Button -->
        <button id="sidebar-workspaces-dd-btn" type="button"
          class="group w-full inline-flex items-center py-3 text-start text-gray-800 align-middle disabled:opacity-50 disabled:pointer-events-none focus:outline-none dark:text-white"
          aria-haspopup="menu" aria-expanded="false" aria-label="Dropdown">
        </button>
        <!-- End Project Button -->

        <!-- Workspaces Dropdown -->
        <div
          class="hs-dropdown-menu hs-dropdown-open:opacity-100 w-56 transition-[opacity,margin] duration opacity-0 hidden z-20 bg-white rounded-xl shadow-[0_10px_40px_10px_rgba(0,0,0,0.08)] dark:shadow-[0_10px_40px_10px_rgba(0,0,0,0.2)] dark:bg-neutral-900"
          role="menu" aria-orientation="vertical" aria-labelledby="sidebar-workspaces-dd-btn">
          <div id="workspaces-dd-list" class="p-1 space-y-0.5">
          </div>
          <div class="p-1">
            <a type="button" id="all-workspaces-link"
              class="w-full flex items-center gap-x-3 py-2 px-3 rounded-lg text-sm text-gray-800 hover:bg-gray-100 disabled:opacity-50 focus:outline-none focus:bg-gray-100 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:focus:bg-neutral-800"
              href="/workspaces" data-navigo>
              View All Workspaces
              <span id="workspaces-dd-count" class="ms-auto text-xs text-gray-500 dark:text-neutral-500">
                8
              </span>
            </a>
          </div>
          <div class="p-1 border-t border-gray-200 dark:border-neutral-800">
            <button id="sidebar-create-workspace" type="button"
              class="hs-tooltip w-full flex items-center gap-x-3 py-2 px-3 rounded-lg text-sm text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:bg-gray-100 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:focus:bg-neutral-800"
              aria-haspopup="dialog">
              <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 12h8" />
                <path d="M12 8v8" />
              </svg>
              New Workspace
              <!-- Tooltip is not required because we call an API before creating a workspace to check eligibility, and if not eligible, showing upgrade plan modal. -->
              <!-- <span
                class="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-10 py-1 px-2 bg-gray-900 text-xs font-medium text-white rounded shadow-sm dark:bg-neutral-700"
                role="tooltip">Please upgrade you plan to <br>create more workspaces</span> -->
            </button>
          </div>
        </div>
        <!-- End Workspaces Dropdown -->
      </div>
    </div>
    `;
  }

  return { render, init, cleanUp };
}